import { logger } from '@adornis/base/logging.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import * as crypto from 'node:crypto';
import { loadAllAndAssert, loadAndAssert, resolveOwnerID } from '../authz/access.js';
import { Exam } from '../exam.js';
import { splitEvaluationResult, Submission } from '../submission.js';

export const createSubmissionMutation = registerMutation({
  operationName: 'createSubmissionMutation',
  type: () => Submission,
  params: [
    { name: 'examId', type: () => String },
    { name: 'imageIds', type: () => [String] },
    { name: 'studentName', type: () => String },
    { name: 'splitConfidence', type: () => Float },
    { name: 'splitIssues', type: () => [String] },
    { name: 'guestUserID', type: () => String },
    { name: 'splitJobId', type: () => String },
  ],

  resolve:
    (
      examId: string,
      imageIds: string[],
      studentName: string,
      splitConfidence?: number,
      splitIssues?: string[],
      guestUserID?: string,
      splitJobId?: string,
    ) =>
    async (_gqlFields: BaseQLSelectionSet<Submission>) => {
      // Also reached from the `submissionSplit` job (server context, no session), which passes the exam owner as
      // `guestUserID`. A client request may only add submissions to its own exam, owned by its own identity — and
      // only with pages it may read: otherwise someone else's scan could be put into an exam and graded (OCR,
      // model) for whoever knows its id. All or nothing.
      await loadAndAssert(Exam, examId, 'update', { _id: 1 });
      await loadAllAndAssert(ImageFile, Array.isArray(imageIds) ? imageIds : [], 'read', { _id: 1 });
      const userID = await resolveOwnerID(guestUserID);
      const { submission, data } = newSubmissionDocument({
        examId,
        imageIds,
        studentName,
        userID,
        splitConfidence,
        splitIssues,
        splitJobId,
      });

      const submissionCollection = await getCollection(Submission);
      await submissionCollection.insertOne(data);
      logger.info({ submissionId: submission._id, examId, context: 'createSubmissionMutation' }, 'Created submission');
      // Return the created Submission entity (BaseQL will handle selection)
      return submission;
    },
});

/** A new submission and its stored form. Shared with the review canvas's saveSubmissionAssignmentMutation. */
export function newSubmissionDocument({
  examId,
  imageIds,
  studentName,
  userID,
  splitConfidence,
  splitIssues,
  splitJobId,
}: {
  examId: string;
  imageIds: string[];
  studentName: string;
  userID: string | null;
  splitConfidence?: number;
  splitIssues?: string[];
  splitJobId?: string;
}) {
  const normalizedSplitConfidence = Number.isFinite(Number(splitConfidence)) ? Number(splitConfidence) : 0;
  const normalizedSplitIssues = Array.isArray(splitIssues) ? splitIssues : [];

  const submission = new Submission({
    _id: crypto.randomUUID(),
    examId,
    submittedAt: new Date().toISOString(),
    imageIds,
    userID,
    studentName,
    // Newly created submissions are editable until the split is confirmed.
    splitConfirmed: false,
    splitEvaluationResult: new splitEvaluationResult({
      confidence: normalizedSplitConfidence,
      issues: normalizedSplitIssues,
    }),
    ...(splitJobId ? { splitJobId } : {}),
  });

  const data = submission.toObject() as any;
  data.splitEvaluationResult = {
    _class: splitEvaluationResult._class,
    confidence: normalizedSplitConfidence,
    issues: normalizedSplitIssues,
  };
  return { submission, data };
}
